import { useEffect, useState } from 'react'
import { ChevronRight, CornerDownLeft, Sparkles } from 'lucide-react'

import {
  FilterDropdownBody,
  FilterDropdownFooter,
} from '@/components/filters/filter-dropdown-shell'
import { FilterOperatorList, FilterPickerPanel } from '@/components/filters/filter-picker-panel'

import { FilterOptionList } from '@/components/filters/filter-menu'
import type { TextFilterOperator } from '@/components/filters/filter-menu'
import type { FilterDefinition, FilterPopoverProps, UnifiedFilterOperator, UnifiedFilterPickerOrigin } from '@/components/filters/list-filter-types'
import { formatUnifiedFilterOperatorPicker } from '@/lib/list-filters/list-filter-params'
import {
  isUnifiedPickerCatalogLoading,
  resolveUnifiedPickerCatalog,
} from '@/lib/list-filters/unified-picker-options'
import {
  formatFilterSuggestionLabel,
  formatTextFilterOperatorPicker,
  resolveFilterOperatorControls,
} from '@/lib/list-filters/unified-filter-labels'
import { FILTER_SUGGESTION_ROW_CLASS } from '@/components/filters/filter-styles'
import { unifiedSuggestionRowId, type UnifiedSuggestion } from '@/lib/list-filters/unified-suggestions'
import { cn } from '@/lib/utils'

const ROW_ACTIVE_CLASS = 'bg-accent text-accent-foreground'

export type UnifiedSuggestionListProps<TFilters, TCtx> = {
  listboxId: string
  suggestions: UnifiedSuggestion<TFilters, TCtx>[]
  activeIndex: number
  filters: TFilters
  /** Shown below suggestions when the user can commit plain text search. */
  searchCommitHint?: string
  onPick: (suggestion: UnifiedSuggestion<TFilters, TCtx>, index: number) => void
  onHover: (index: number) => void
}

/** Mode `suggest`: value rows, category rows, semantic row, plain-search hint. */
export function UnifiedSuggestionList<TFilters, TCtx>({
  listboxId,
  suggestions,
  activeIndex,
  filters,
  searchCommitHint,
  onPick,
  onHover,
}: UnifiedSuggestionListProps<TFilters, TCtx>) {
  return (
    <div id={listboxId} role="listbox" aria-label="Filter suggestions" className="max-h-72 space-y-0.5 overflow-y-auto p-1.5">
      {suggestions.map((suggestion, index) => {
        const active = index === activeIndex
        const rowProps = {
          id: unifiedSuggestionRowId(listboxId, index),
          role: 'option' as const,
          'aria-selected': active,
          className: cn(FILTER_SUGGESTION_ROW_CLASS, active && ROW_ACTIVE_CLASS),
          // preventDefault keeps DOM focus pinned to the bar's input
          onMouseDown: (event: React.MouseEvent) => event.preventDefault(),
          onClick: () => onPick(suggestion, index),
          onMouseEnter: () => onHover(index),
        }

        if (suggestion.kind === 'value') {
          return (
            <button type="button" key={`value-${suggestion.def.key}-${suggestion.option.value}`} {...rowProps}>
              <span className="truncate font-medium">
                {formatFilterSuggestionLabel(suggestion.def, filters, suggestion.option)}
              </span>
              {typeof suggestion.option.count === 'number' ? (
                <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                  {suggestion.option.count}
                </span>
              ) : null}
              {active ? <EnterHint className={typeof suggestion.option.count === 'number' ? '' : 'ml-auto'} /> : null}
            </button>
          )
        }

        if (suggestion.kind === 'category') {
          const Icon = suggestion.def.icon
          return (
            <button type="button" key={`category-${suggestion.def.key}`} {...rowProps}>
              <IconTile active={active}>
                {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
              </IconTile>
              <span className="truncate">{suggestion.def.label}</span>
              {active ? <EnterHint className="ml-auto" /> : null}
              <ChevronRight
                className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground', !active && 'ml-auto')}
                aria-hidden
              />
            </button>
          )
        }

        return (
          <button type="button" key="semantic" {...rowProps}>
            <IconTile active={active}>
              <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
            </IconTile>
            <span className="truncate text-muted-foreground">
              Ask about <span className="font-semibold text-foreground">&ldquo;{suggestion.query}&rdquo;</span>
            </span>
            {active ? <EnterHint className="ml-auto" /> : null}
          </button>
        )
      })}
      {searchCommitHint ? (
        <div className="px-2.5 py-2 text-xs italic text-muted-foreground">{searchCommitHint}</div>
      ) : null}
    </div>
  )
}

function IconTile({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors',
        active ? 'bg-primary/10 text-primary' : 'bg-muted/60',
      )}
      aria-hidden
    >
      {children}
    </span>
  )
}

function EnterHint({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-sm border border-border/60 bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground',
        className,
      )}
      aria-hidden
    >
      <CornerDownLeft className="h-2.5 w-2.5" />
      Enter
    </span>
  )
}

export type UnifiedValuePickerProps<TFilters, TCtx> = {
  def: FilterDefinition<TFilters, TCtx>
  popoverProps: FilterPopoverProps<TFilters, TCtx>
  pickerOrigin: UnifiedFilterPickerOrigin
  onBack: () => void
  /** Fired after a single-select value is applied — the bar chains back to the suggestion list. */
  onSinglePicked?: () => void
}

function renderPickerBody<TFilters, TCtx>(
  def: FilterDefinition<TFilters, TCtx>,
  popoverProps: FilterPopoverProps<TFilters, TCtx>,
  pickerOrigin: UnifiedFilterPickerOrigin,
  pickerSearchQuery: string,
  onPickerSearchQueryChange: (query: string) => void,
  onSinglePicked?: () => void,
) {
  const { filters, onChange, ctx } = popoverProps

  if (!def.unified) {
    return def.renderPopover?.(popoverProps) ?? null
  }

  if (def.unified.picker === 'custom') {
    return def.renderPopover?.(popoverProps) ?? null
  }

  const catalog = resolveUnifiedPickerCatalog(def.unified, ctx, filters, { origin: pickerOrigin })
  const loading = isUnifiedPickerCatalogLoading(def.unified, ctx, catalog, filters)

  if (loading) {
    return (
      <div className="px-2.5 py-3 text-sm text-muted-foreground" aria-live="polite">
        Loading options…
      </div>
    )
  }

  if (def.unified.selection === 'multi') {
    return (
      <FilterOptionList
        type="multi"
        options={catalog}
        value={def.unified.getSelectedValues(filters, ctx)}
        onValueChange={(values) => onChange(def.unified!.setSelectedValues(filters, values, ctx))}
        searchPlaceholder={def.unified.searchPlaceholder}
        emptyLabel={def.unified.emptyLabel}
        searchQuery={pickerSearchQuery}
        onSearchQueryChange={onPickerSearchQueryChange}
      />
    )
  }

  return (
    <FilterOptionList
      type="single"
      options={catalog}
      value={def.unified.getSelectedValues(filters, ctx)[0] ?? ''}
      onValueChange={(value) => {
        onChange(def.unified!.setSelectedValues(filters, value ? [value] : [], ctx))
        onSinglePicked?.()
      }}
      searchPlaceholder={def.unified.searchPlaceholder}
      emptyLabel={def.unified.emptyLabel}
      searchQuery={pickerSearchQuery}
      onSearchQueryChange={onPickerSearchQueryChange}
    />
  )
}

/** Mode `picker`: back header + native option list (or renderPopover fallback) + operator footer. */
export function UnifiedValuePicker<TFilters, TCtx>({
  def,
  popoverProps,
  pickerOrigin,
  onBack,
  onSinglePicked,
}: UnifiedValuePickerProps<TFilters, TCtx>) {
  const { filters, onChange } = popoverProps
  const [pickerSearchQuery, setPickerSearchQuery] = useState('')

  useEffect(() => {
    setPickerSearchQuery('')
  }, [def.key, pickerOrigin])

  const operatorControls = resolveFilterOperatorControls(def)

  const formatPickerOperatorLabel = (op: string) =>
    operatorControls?.family === 'text'
      ? formatTextFilterOperatorPicker(op as TextFilterOperator)
      : formatUnifiedFilterOperatorPicker(op as UnifiedFilterOperator)

  const operatorFooter =
    operatorControls && def.unified?.picker !== 'custom' ? (
      <FilterOperatorList
        ariaLabel={`${def.label} operator`}
        options={operatorControls.operators.map((op) => ({
          value: op,
          label: formatPickerOperatorLabel(op),
        }))}
        value={operatorControls.getOperator(filters)}
        onValueChange={(op: string) => onChange(operatorControls.setOperator(filters, op))}
      />
    ) : undefined

  const pickerFooterContent = def.pickerFooter?.(popoverProps)
  const footer =
    operatorFooter ??
    (pickerFooterContent ? <FilterDropdownFooter>{pickerFooterContent}</FilterDropdownFooter> : undefined)

  const pickerBody = renderPickerBody(
    def,
    popoverProps,
    pickerOrigin,
    pickerSearchQuery,
    setPickerSearchQuery,
    onSinglePicked,
  )
  const body =
    def.unified && def.unified.picker !== 'custom' ? (
      <FilterDropdownBody>{pickerBody}</FilterDropdownBody>
    ) : (
      pickerBody
    )

  return (
    <FilterPickerPanel title={def.label} onBack={onBack} footer={footer}>
      {body}
    </FilterPickerPanel>
  )
}
